/**
 * 加购按钮上的金额（May 2026-08-26 的 二.8）。
 *
 * 按钮长这样：`Add to Order List │ $12.56`，已经加进订单清单的换成 `2 in order │ $25.12`。
 *
 * 服务端渲染出第一版（buy-buttons.liquid），这里只管两件会变的事：
 *   ① 数量框一改，金额跟着乘
 *   ② 加购成功之后，左边文字翻成「N in order」—— Dawn 加购后**不会重渲染商品区**，
 *      不接这个事件的话要刷新页面才看得到，她会以为没加上
 *
 * ⚠️ **不自己造币种格式**。金额的写法（符号在前在后、小数点还是逗号）跟着
 * 店铺设置走，服务端已经渲染对了一次 —— 这里把那一版拆成「前缀 + 数字 + 后缀」，
 * 只换中间的数字。自己拼 `'$' + n` 在欧元/阿语站点上就是错的。
 */
(function () {
  const SEL = '.torena-cta';

  /**
   * 从服务端渲染好的那串金额里，学会这个店的写法。
   *
   * ⚠️ 切法是「第一个数字到最后一个数字」，前后原样当前缀后缀。
   * 别拿 `[\d.,\s]+` 去匹配数字段 —— 它会把 `12,56 €` 里数字和 € 之间那个空格
   * 吞进数字里，前缀后缀就错位了（实测踩过）。
   */
  function moneyShape(text) {
    const s = String(text);
    const first = s.search(/\d/);
    if (first < 0) return null;
    let last = first;
    for (let i = s.length - 1; i >= first; i--) {
      if (/\d/.test(s[i])) { last = i; break; }
    }
    const core = s.slice(first, last + 1);
    // 小数分隔符：结尾那个后面跟着 1~2 位数字的 . 或 ,
    const dec = /[.,](\d{1,2})$/.exec(core);
    const decimalSep = dec ? core[core.length - dec[1].length - 1] : '.';
    // 千分位：数字之间出现过的、和小数分隔符**不同**的那个符号。
    // 一个都没出现过（"$12.56" 这种）就取相反的那个，别和小数分隔符撞成同一个 ——
    // 撞了的话 1256.00 会排成 "1,256,00"
    const others = [...core.replace(/\d/g, '')].reverse().filter((c) => c !== decimalSep);
    return {
      prefix: s.slice(0, first),
      suffix: s.slice(last + 1),
      decimalSep,
      groupSep: others[0] || (decimalSep === ',' ? '.' : ','),
      decimals: dec ? dec[1].length : 2,
    };
  }

  function render(shape, cents) {
    const n = (cents / 100).toFixed(shape.decimals);
    const [int, frac] = n.split('.');
    const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, shape.groupSep);
    return shape.prefix + grouped + (frac ? shape.decimalSep + frac : '') + shape.suffix;
  }

  function update(cta, qty, inCart) {
    const amount = cta.querySelector('.torena-cta__amount');
    const label = cta.querySelector('.torena-cta__label');
    const unit = parseInt(cta.dataset.unitPrice, 10);

    if (inCart != null) {
      cta.dataset.inCart = String(inCart);
      // 已经在订单清单里 → 左边报数量;清空了 → 回到「Add to Order List」
      if (label && cta.dataset.addLabel) {
        label.textContent = inCart > 0 ? `${inCart} in order` : cta.dataset.addLabel;
      }
    }
    if (!amount || !Number.isFinite(unit)) return;
    const shape = cta._shape || (cta._shape = moneyShape(amount.textContent));
    if (!shape) return;
    const n = Math.max(1, parseInt(qty, 10) || 1);
    amount.textContent = render(shape, unit * n);
  }

  /** 这个按钮对应的数量框。同一张表单里的那个才算，别抓到别的商品的 */
  function qtyInputFor(cta) {
    const form = cta.closest('product-form, .product-form, form');
    const root = form?.closest('product-info') || document;
    return root.querySelector('.quantity__input, input[name="quantity"]');
  }

  function wire(cta) {
    if (cta._wired) return;
    cta._wired = true;
    const label = cta.querySelector('.torena-cta__label');
    // 记下「还没加购」时的原文，之后从清单里删干净了要还原回去
    if (label && !cta.dataset.addLabel && !Number(cta.dataset.inCart)) {
      cta.dataset.addLabel = label.textContent.trim();
    }
    const qty = qtyInputFor(cta);
    if (!qty) return;
    const onInput = () => update(cta, qty.value, null);
    qty.addEventListener('change', onInput);
    qty.addEventListener('input', onInput);
    // 服务端已经按「在清单里的数量」算过一次了，别在这儿重算覆盖掉
    if (!Number(cta.dataset.inCart)) update(cta, qty.value, null);
  }

  const wireAll = () => document.querySelectorAll(SEL).forEach(wire);

  document.addEventListener('DOMContentLoaded', wireAll);
  wireAll();

  // 换变体：Dawn 会把整块 HTML 换成服务端新渲染的，重新接一次
  document.addEventListener('product-info:loaded', wireAll);

  // 加购之后：Dawn 不重渲染商品区，得自己把「N in order」补上
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS === 'object') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      const items = event?.cartData?.items;
      document.querySelectorAll(SEL).forEach((cta) => {
        const root = cta.closest('product-info') || document;
        const id = root.querySelector('input[name="id"]')?.value;
        if (!id) return;
        // 拿不到 items（有些来源只回 sections）就只更新金额，不乱报数量
        const inCart = Array.isArray(items)
          ? (items.find((i) => String(i.variant_id) === String(id))?.quantity ?? 0)
          : null;
        const qty = qtyInputFor(cta);
        update(cta, inCart || qty?.value || 1, inCart);
      });
    });
  }
})();
